import { useEffect, useState } from "react";
import { parseISO, isBefore, startOfMonth, endOfMonth, format } from 'date-fns';
import TitleRecordFunction from "../../components/TitleRecordFunction";
import api from '../../api/manage_database/FlowMeterReq';
import apii from '../../api/manage_database/FlowMeasurementReq';
import BackButton from "../../components/BackButton";
import Spinner from "../../components/Spinner";
import { toast } from 'sonner';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import SelectPlace from "../../components/SelectPlace";
import SelectThoroughfare from "../../components/SelectThroughfare";
import SelectTypeLines from "../../components/SelectTypeLines";
import DateInput from "../../components/InputDate";
import SelectParameter from "../../components/SelectParameter";

const FlowMeasurementFastRecord = () => {
    const [loadingState, setLoadingState] = useState(false);
    const [flowMeters, setFlowMeters] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState('');
    const [places, setPlaces] = useState([]);
    const [selectedThoroughfare, setSelectedThoroughfare] = useState('');
    const [thoroughfares, setThoroughfares] = useState([]);
    const [selectedTypeLine, setSelectedTypeLine] = useState('');
    const [typeLines, setTypeLines] = useState([]);
    const [data, setData] = useState({});

    const chartOrder = [
        'Flow',
        'DT',
        'TTUp',
        'TTDn',
        'FlowVelocity',
        'AbsoluteSoundVelocity',
        'SoundVelocity',
        'AbsoluteRe',
        'Re',
        'SignalAmplitudeUp',
        'SignalAmplitudeDn',
        'RxGainUp',
        'RxGainDn',
        'SNRUp',
        'SNRDn',
        'CorrelationCoef',
        'ReferenceCoef',
        'PcbTemp',
        'ExternalInput',
        'BurstsAccepted',
        'Temp',
        'StDev',
        'MeanFlow',
    ];

    const [chartName, setChartName] = useState(chartOrder[0]);
    const firstDay = startOfMonth(new Date());
    const lastDay = endOfMonth(new Date());
    const [selectedFromDate, setSelectedFromDate] = useState(format(firstDay, 'yyyy-MM-dd'));
    const [selectedToDate, setSelectedToDate] = useState(format(lastDay, 'yyyy-MM-dd'));

    const handleChangeFromDate = (event) => {
        setSelectedFromDate(event.target.value)
    };

    const handleChangeToDate = (event) => {
        setSelectedToDate(event.target.value)
    };

    const handleFindButton = () => {
        if (!selectedFromDate || !selectedToDate) {
            toast.error('Error! Insert all dates!');
            return
        }

        const dateFromParsed = parseISO(selectedFromDate);
        const dateToParsed = parseISO(selectedToDate);

        if (isBefore(dateToParsed, dateFromParsed)) {
            toast.error('Error! First date is not earlier!')
            return
        }

        const filteredFlowMeterId = flowMeters
            .filter(
                flowmeter => flowmeter.location_id.place === selectedPlace &&
                flowmeter.location_id.thoroughfare === selectedThoroughfare &&
                flowmeter.location_id.type_line === selectedTypeLine)
            .map((flowmeter) => (flowmeter.id))[0]

        if (!filteredFlowMeterId) {
            toast.error('Error! Select flow meter!');
            return
        }

        const fetchAvailableMeasurements = async () => {
            setLoadingState(true);
            try {
                const response = await apii.get(
                    `/fast-available-measurements/${filteredFlowMeterId}/${chartName}/${selectedFromDate}/${selectedToDate}`,
                    {withCredentials: true}
                );
                if (response.data.success) {
                    setData(response.data.payload);
                } else {
                    toast.error(response.data.message);
                }
            } catch (err) {
                console.log(err.response);
                toast.error('Error! Fast search failed!');
            }
            setLoadingState(false);
        };
        fetchAvailableMeasurements();
    }

    useEffect(() => {
        const fetchFlowMeters = async () => {
            try {
                const response = await api.get('all', {withCredentials: true});
                if (response.data.success) {
                    setFlowMeters(response.data.payload);
                } else {
                    console.error('Error:', response.data.message)
                }
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchFlowMeters();
    }, []);

    useEffect(() => {
        const handlePlaces = () => {
            const newplaces = [...new Set(flowMeters.map(item => item.location_id.place))]
            return newplaces.map((name, index) => ({id: index+1, name: name}));
        }
        if (flowMeters.length > 0) {
            setPlaces(handlePlaces());
        }
    }, [flowMeters])

    useEffect(() => {
        const filterPlaces = () => {
            const newthoroughfare = flowMeters
                .filter(flowmeter => flowmeter.location_id.place === selectedPlace)
                .map((fl, index) => ({id: index+1, name: fl.location_id.thoroughfare}))
            return newthoroughfare.reduce((acc, current) => {
                if (!acc.some(item => item.name === current.name)) {
                    acc.push(current);
                }
                return acc
            }, [])
        }
        if (flowMeters.length > 0) {
            setThoroughfares(filterPlaces())
        }
    }, [selectedPlace, flowMeters])

    useEffect(() => {
        const filterPlacesAndThoroughfares = () => {
            const newTypeLines = flowMeters
                .filter(flowmeter => flowmeter.location_id.place === selectedPlace && flowmeter.location_id.thoroughfare === selectedThoroughfare)
                .map((fl, index) => ({id: index+1, name: fl.location_id.type_line}))

            return newTypeLines.reduce((acc, current) => {
                if (!acc.some(item => item.name === current.name)) {
                    acc.push(current);
                }
                return acc
            }, [])
        }
        if (flowMeters.length > 0) {
            setTypeLines(filterPlacesAndThoroughfares())
        }
    }, [selectedThoroughfare, selectedPlace, flowMeters]);

   return (
        <div className="flow-measurement-record-container">
            <TitleRecordFunction
                addRecordMessage={""}
                deleteRecordMessage={""}
                title='Flow Measurement Fast Search'
                isViewRecord={true}
            />
            <div className="flow-measurement-record-card">
                <div className="choice-input-group">
                    <label htmlFor='' className='bold-label'>Place:</label>
                    <SelectPlace
                        places={places}
                        selectedPlace={selectedPlace}
                        setSelectedPlace={setSelectedPlace}
                    />
                </div>
                <div className="choice-input-group">
                    <label htmlFor='' className='bold-label'>Thoroughfare:</label>
                    <SelectThoroughfare
                        thoroughfares={thoroughfares}
                        selectedThoroughfare={selectedThoroughfare}
                        setSelectedThoroughfare={setSelectedThoroughfare}
                    />
                </div>
                <div className="choice-input-group">
                    <label htmlFor='' className='bold-label'>Type Line:</label>
                    <SelectTypeLines
                        LocationTypeLines={typeLines}
                        selectedlocationTypeLine={selectedTypeLine}
                        setSelectedLocationTypeLine={setSelectedTypeLine}
                    />
                </div>
                <div className="choice-input-group">
                    <label htmlFor='' className='bold-label'>Param:</label>
                    <SelectParameter
                        Parameters={chartOrder}
                        selectedParameter={chartName}
                        setSelectedParameter={setChartName}
                    />
                </div>
                <div className="choice-input-group">
                    <label htmlFor='' className='bold-label'>FROM:</label>
                    <DateInput
                        selectedDate={selectedFromDate}
                        handleSelectedDate={handleChangeFromDate}
                    />
                </div>
                <div className="choice-input-group">
                    <label htmlFor='' className='bold-label'>TO:</label>
                    <DateInput
                        selectedDate={selectedToDate}
                        handleSelectedDate={handleChangeToDate}
                    />
                </div>
                {!loadingState ? <button className='menu-button' onClick={handleFindButton}>
                    Find <FaMagnifyingGlass />
                </button> : <button className='menu-button'>
                    <Spinner size="small" />
                </button>}
            </div>
            {!loadingState ? <div className="table-wrapper flow-measurement-fast-table-wrapper">
                <table className="flow-measurement-record-table-style flow-measurement-fast-table text-align-center">
                    <tbody>
                        <tr>
                            <td>Date</td>
                            <td>Data</td>
                        </tr>
                        {Object.entries(data).map(([date, isAvailable]) => (
                            <tr key={date}>
                                <td>{date}</td>
                                {isAvailable ?
                                    <td className="td-green">true</td> :
                                    <td className="td-red">false</td>
                                }
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>: null}
            <BackButton navigateTo='/manage-database/flow-measurement/choice'/>
        </div>
    );
}

export default FlowMeasurementFastRecord;
